import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { IconButton, Stack } from '@mui/material';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import strings from '../Globalization/index';
import consts from '../consts';
import Model from '../models/model';
import connectionService from '../services/Connection';
import util from '../util';
import { dataStore } from '../Store';

const isLimitedUser = () => {
	const roles = util.localData?.loginData?.roles;
	const isLimited = Array.isArray(roles) ? roles.includes('Supervisor') : roles === 'Supervisor';
	return isLimited;
};


const superAdminDisabledField = ({ formik, activeRecord }) => {
	if (activeRecord.id) {
		const allowed = activeRecord?.combos?.UserType.filter((entry) => entry.IsApplication).map((entry) => entry.LookupId);
		return !allowed.includes(formik.values.UserTypeId) || util.getRoleValues().superAdministratorId === formik.values.RoleId;
	}
};

const superAdminReadOnly = ({ activeRecord }) => {
	if (activeRecord && activeRecord.record) {
		const { RoleId } = activeRecord.record;
		return util.getRoleValues().superAdministratorId === RoleId;
	}
};
const showClientField = ({ activeRecord }) => {
	const { id, record = {} } = activeRecord;
	const { auth = {} } = dataStore.getState();
	// client field will be shown only for update, core license and STERIS Admin or Superadmin role and can't be added for SuperAdmin user
	return id && (auth.license.type === consts.licenseType.core) && ([consts.RoleName.SterisAdmin, consts.RoleName.Superadmin].includes(auth.tags.RoleName)) && (record.RoleName !== consts.RoleName.Superadmin);
}

export const getPasswordStrengthTest = (strength, errorMsg, defaultStrength) => {
	return {
		name: `password-strength-${strength.toLowerCase()}`,
		message: strings[errorMsg],
		test: (value, { parent }) => {
			if (parent.RoleId && parent.RoleId === util.getRoleValues().superAdministratorId) {
				return true;
			}
			let shouldValidate = false;
			const passwordRegExp = util.getPasswordRegExp('PasswordStrength') || defaultStrength;
			const strengthConst = consts.PasswordStrength[strength];
			if ((passwordRegExp === strengthConst && ( parent.id === 0 || value !== '******' && !defaultStrength))) {
				shouldValidate = true;
			}
			return shouldValidate || parent.alreadyHaveToken ? new RegExp(strengthConst).test(value) : true;
		}
	}

}

const model = new Model({
	name: 'User',
	readOnly: superAdminReadOnly,
	disableDelete: superAdminReadOnly,
	defaultSort: 'Username',
	title: 'User',
	showGridRefreshBtn: true,
	customizeSaveData: (toSave, { activeRecord, formik }) => {
		delete toSave.IsLocked;
		toSave.UserLocations = JSON.stringify((toSave.UserLocations ? JSON.parse(toSave.UserLocations) : []).map(id => ({ Id: '0', LocationId: id })));
		toSave.RoleId = toSave.RoleId ? toSave.RoleId : 0;
		return toSave;
	},
	formTitleField: 'Username',
	initialValues: {
		IsActive: true,
		CycleReviewAccess: true
	},
	formGroups: [
		{
			fields: [
				'UserTypeId',
				'Username',
				'Password',
				'ConfirmPassword',
				'RoleId',
				'PrimaryEmail',
				'UserLocations',
				'ClientId',
				'IsActive',
				'CycleReviewAccess',
				'IsLocked',
			]
		}
	],
	onFormLoad: async ({ formik, activeRecord }) => {
		if ((activeRecord?.id !== null) & (activeRecord?.record?.UserTypeId !== '')) {
			const UserTypeId = activeRecord?.record?.UserTypeId;
			const UserTypeName = activeRecord?.combos?.UserType.filter((obj) => {
				return obj.LookupId === UserTypeId;
			});
			if (UserTypeName !== undefined) {
				formik.setFieldValue('UserTypeName', UserTypeName[0].DisplayValue);
				if (activeRecord?.record?.RoleId === util.getRoleValues().superAdminId) {
					formik.setFieldValue('CycleReviewAccess', false);
				}
			}
		}
	},
	columns: [
		{
			header: 'UserType',
			field: 'UserType',
			width: 120,
			lookup: 'UserType',
			formField: 'UserTypeId',
			resets: ['Password', 'ConfirmPassword', 'RoleId'],
			hide: true,
			required: true,
			excludeOnGrid: true,
			isNotformfield: true,
			readOnly: [
				{
					field: 'RoleId',
					op: 'in',
					value: () => [util.getRoleValues().superAdministratorId]
				}
			],
			onChange: ({ formik, value, activeRecord }) => {
				const UserType = activeRecord.combos.UserType?.filter((obj) => {
					return obj.LookupId === value;
				});
				formik.setFieldValue('PrimaryEmail', '');
				formik.setFieldValue('ConfirmPassword', '');
				formik.setFieldValue('Password', '');
				formik.setFieldValue('RoleId', '');
				formik.setFieldValue('UserTypeName', UserType[0]?.DisplayValue);
			}
		},
		{ field: 'UserId', header: 'UniqueId', width: 120, isNotformfield: true, hide: true },
		{
			field: 'Username',
			width: 140,
			required: true,
			minLength: 2,
			maxLength: 50,
			readOnly: [
				{
					field: 'RoleId',
					op: 'in',
					value: () => [util.getRoleValues().superAdministratorId]
				}
			],
			onChange: ({ formik, activeRecord }) => {
				const { LookupId: UserTypeId, DisplayValue: UserTypeName } =
					activeRecord.combos.UserType?.find((entry) => entry.IsApplication) || {};
				formik.setFieldValue('UserTypeId', UserTypeId);
				formik.setFieldValue('UserTypeName', UserTypeName);
			}
		},
		{
			header: 'Password',
			field: 'Password',
			type: 'password',
			width: 120,
			hidden: true,
			excludeOnGrid: true,
			skipPasswordStrengthCheck: true,
			required: [
				{
					field: 'UserTypeId',
					op: 'in',
					value: ({ activeRecord }) => {
						let allowed = [];
						if (Array.isArray(activeRecord?.combos?.UserType)) {
							allowed = activeRecord?.combos?.UserType.filter((entry) => entry.IsApplication).map(
								(entry) => entry.LookupId
							);
						}
						return allowed;
					}
				},
				{
					field: 'RoleId',
					op: 'nin',
					value: () => {
						return [util.getRoleValues().superAdministratorId];
					}
				}
			],
			minLength: ({ activeRecord }) => (activeRecord?.id ? 0 : 6),
			maxLength: 50,
			validate: () => {
				let schema = yup.string();
				Object.keys(consts.PasswordStrength).forEach(strength => {
					const testObj = getPasswordStrengthTest(strength, consts.passwordStringMapper[strength]);
					schema = schema.test(testObj);
				});

				return schema;
			},
			readOnly: superAdminDisabledField
		},
		{
			header: 'ConfirmPassword',
			field: 'ConfirmPassword',
			type: 'password',
			excludeOnGrid: true,
			width: 120,
			hidden: true,
			required: [
				{
					field: 'UserTypeId',
					op: 'in',
					value: ({ activeRecord }) => {
						let allowed = [];
						if (Array.isArray(activeRecord?.combos?.UserType)) {
							allowed = activeRecord?.combos?.UserType.filter((entry) => entry.IsApplication).map(
								(entry) => entry.LookupId
							);
						}
						return allowed;
					}
				},
				{
					field: 'RoleId',
					op: 'nin',
					value: () => {
						return [util.getRoleValues().superAdministratorId];
					}
				}
			],
			readOnly: superAdminDisabledField,
			validate: 'compare:Password'
		},
		{
			field: 'PrimaryEmail',
			width: 290,
			maxLength: 50,
			validate: 'isEmail',
			required: [
				{
					field: 'RoleId',
					op: 'in',
					value: () => [
						util.getRoleValues().superAdminId,
						util.getRoleValues().supervisorRoleId,
						util.getRoleValues().superAdministratorId,
						util.getRoleValues().huluAdminId,
						util.getRoleValues().sterisSpecialistId
					]
				}
			],
			readOnly: [
				{
					field: 'RoleId',
					op: 'nin',
					value: () => [
						util.getRoleValues().superAdminId,
						util.getRoleValues().supervisorRoleId,
						util.getRoleValues().huluAdminId,
						util.getRoleValues().sterisSpecialistId
					]
				}
			]
		},
		{
			header: 'Role',
			field: 'RoleName',
			formField: 'RoleId',
			ignoreLookupOnGridFilter: true,
			width: 150,
			lookup: 'Role',
			resets: ['PrimaryEmail', 'UserLocations', 'IsGeneric', 'CanReview'],
			readOnly: ({ activeRecord, formik }) => {
				if (activeRecord.id) {
					const allowed = activeRecord?.combos?.UserType.filter((entry) => entry.IsApplication).map(
						(entry) => entry.LookupId
					);
					return (
						!allowed.includes(formik.values.UserTypeId) ||
						util.getRoleValues().superAdministratorId === formik.values.RoleId
					);
				}
			},
			required: [
				{
					field: 'UserTypeId',
					op: 'in',
					value: ({ activeRecord }) => {
						let allowed = [];
						if (Array.isArray(activeRecord?.combos?.UserType)) {
							allowed = activeRecord?.combos?.UserType.filter((entry) => entry.IsApplication).map(
								(entry) => entry.LookupId
							);
						}
						return allowed;
					}
				}
			],
			onChange: ({ formik, value }) => {
				const roles = util.getRoleValues();
				if ([roles.departmentId, roles.physicianAdminId].includes(value)) {
					formik.setFieldValue('CycleReviewAccess', false);
				}
				else if ([roles.superAdminId, roles.supervisorRoleId, roles.operatorId].includes(value)) {
					formik.setFieldValue('CycleReviewAccess', true);
				}
				if ([roles.operatorId, roles.departmentId, roles.viewerId, roles.administratorId].includes(value)) {
					formik.setFieldValue('PrimaryEmail', '');
				}
			}
		},

		{
			header: 'Location',
			field: 'UserLocations',
			width: 150,
			lookup: 'Location',
			multiple: true,
			hide: true,
			excludeOnGrid: true,
			filterSelectedOptions: true,
			autoHighlight: true,
			getOptionDisabled: (option, { value }) => {
				if (isLimitedUser()) {
					return Array.isArray(value) && value?.length > 0 && value.findIndex((val) => val.id !== option.id);
				}

				return false;
			},
			inputWhen: [
				{
					field: 'RoleId',
					op: 'nin',
					value: () => {
						return [
							util.getRoleValues().viewerId,
							util.getRoleValues().administratorId,
							util.getRoleValues().huluAdminId
						];
					}
				}
			],
			renderCell: (params) => {
				let locations = [];
				try {
					const { Location } = params.colDef.getCombos();
					locations = (params.row.UserLocations ? JSON.parse(params.row.UserLocations) : []).map(
						(location) => Location.find((Loc) => Loc.LookupId === location)?.DisplayValue
					);
				} catch (error) { /* empty */ }
				return locations.join(',');
			}
		},
		{
			field: 'LastLoginOn',
			type: 'dateTime',
			width: 200,
			isLocal: true,
			filterType: 'date',
			isNotformfield: true,
			format: 'MM/DD/YYYY hh:mm:ss A'
		},
		{
			header: 'Active',
			field: 'IsActive',
			type: 'boolean',
			width: 120,
			readOnly: [
				{
					field: 'RoleId',
					op: 'in',
					value: () => [util.getRoleValues().superAdministratorId]
				}
			]
		},
		{
			header: 'CanReview',
			field: 'CycleReviewAccess',
			type: 'bool',
			width: 135,
			defaultValue: true,
			excludeOnGrid: () => {
				const licenseData = util.isLicenseType('catviewer');
				return !licenseData
			},
			inputWhen: [
				{
					field: 'RoleId',
					op: ({ value }) => {
						const roles = util.getRoleValues();
						const licenseData = util.isLicenseType('catviewer');
						return licenseData && ![roles.viewerId, roles.administratorId, roles.huluAdminId].includes(value);
					}
				}
			],
			disabled: [
				{
					field: 'RoleId',
					op: ({ value }) => {
						const roles = util.getRoleValues();
						return [roles.departmentId, roles.superAdminId, roles.supervisorRoleId].includes(value); //Disable for Department, SuperAdmin and Supervisor Roles
					}
				}
			],
			readOnly: [
				{
					field: 'RoleId',
					op: ({ value }) => {
						const roles = util.getRoleValues();
						return [roles.superAdminId, roles.supervisorRoleId].includes(value); //Read only for supervisor and Steris admin Roles
					}
				}
			]
		},
		{
			header: 'AccountStatus',
			field: 'IsLocked',
			type: 'bool',
			customfilterValue: { trueDisplay: 'Locked', falseDisplay: 'Unlocked' },
			width: 140,
			options: [
				{ id: consts.AccountStatus.Locked, label: strings.Locked, Icon: LockIcon },
				{ id: consts.AccountStatus.Unlocked, label: strings.Unlocked, Icon: LockOpenIcon }
			],
			inputWhen: ({ activeRecord }) => {
				return !!activeRecord.id;
			},
			renderCell: (params) => {
				if (params.value === false) {
					return 'Unlocked';
				} else {
					return 'Locked';
				}
			},
			customizeInput: ({ inputProps: { formik, field, column, activeRecord } }) => {
				if (formik.values.IsLocked === '') {
					const lockStatus = activeRecord.record.IsLocked;
					setTimeout(() => {
						formik.setFieldValue(field, lockStatus);
					}, 0); // Defer state update to the next event loop to avoid updating Formik state during React's render phase
				}
				const handleOnClick = (val) => () => {
					if (val === consts.AccountStatus.Locked) {
						Swal.fire({ html: strings.LockAccount, icon: 'info', title: 'Info', confirmButtonText: strings.Ok, allowOutsideClick: false });
					} else {
						formik.setFieldValue(field, val);
						const formData = new FormData();
						formData.append('action', 'otherAction');
						formData.append('otherAction', 'UnlockAccount');
						formData.append('unlockUserId', activeRecord.id);
						fetch(connectionService.User, {
							method: 'POST',
							body: formData
						}).catch((err) =>
							Swal.fire({ html: strings.ErrorAtUnlock, icon: 'error', title: strings.ErrorTitle, confirmButtonText: strings.Ok, allowOutsideClick: false })
						);
					}
				};
				return (
					<Stack
						direction="row"
						columnGap={2}
						sx={{
							'& .optionHighlighted': {
								backgroundColor: (theme) => theme.palette.divider
							}
						}}
					>
						{column.options.map(({ id, label, Icon }, index) => (
							<IconButton
								type="button"
								size={'large'}
								className={Boolean(formik.values[field]) === Boolean(id) ? 'optionHighlighted' : ''}
								onClick={handleOnClick(id)}
								title={label}
								key={`option-${index}`}
							>
								<Icon fontSize="inherit" />
							</IconButton>
						))}
					</Stack>
				);
			}
		},
		{
			field: 'UserTypeName',
			excludeOnGrid: true,
			isNotformfield: true
		},
		{
			field: 'ClientId',
			header: 'Client',
			lookup: 'Clients',
			excludeOnGrid: true,
			width: 150,
			addable: ({ activeRecord }) =>
				activeRecord?.record?.id ? `/userpasswordform?id=new${activeRecord?.record?.id ? '&Username=' + activeRecord?.record?.Username : ''
					}&returnTo=${encodeURIComponent(`/userlist?id=${activeRecord?.record?.id || 'new'}`)}` : false,
			inputWhen: showClientField,
			addableDisabled: false,
			readOnly: ({ activeRecord }) => activeRecord?.record?.id,
		}
	]
});
export default model.createFormBase();
